import React from 'react';
import { useSearchParams } from 'react-router-dom';

export const useTypedSearchParams = <T extends object>(
  defaultValue?: T,
): [T, (value: T | ((original: T) => T)) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = React.useMemo((): T => {
    const result: Record<string, string | string[]> = {};

    // Group all values by their keys
    for (const [key, value] of searchParams.entries()) {
      if (key in result) {
        // If we already have this key, convert it to an array if needed
        if (Array.isArray(result[key])) {
          (result[key] as string[]).push(value);
        } else {
          result[key] = [result[key] as string, value];
        }
      } else {
        result[key] = value;
      }
    }

    return result as T;
  }, [searchParams]);

  const currentValue = React.useMemo(
    (): T => ({ ...defaultValue, ...value }),
    [defaultValue, value],
  );

  const setValue = React.useCallback(
    (value: T | ((value: T) => T)): void => {
      const search = new URLSearchParams();

      const newValue = typeof value === 'function' ? value(currentValue) : value;

      for (const [key, itemValue] of Object.entries(newValue)) {
        // Skip undefined, null, and empty string values
        if (itemValue === undefined || itemValue === null || itemValue === '') {
          continue;
        }

        if (Array.isArray(itemValue)) {
          // Handle array values by appending multiple entries
          itemValue.forEach((val: string): void => {
            search.append(key, val);
          });
        } else {
          search.set(key, itemValue);
        }
      }

      setSearchParams(search);
    },
    [currentValue, setSearchParams],
  );

  return [currentValue, setValue];
};
